import { Box, Container, styled } from '@mui/material';
import {
  CompletedEvaluation,
  FlattenedCompletedEvaluation,
  useGetCompletedEvaluators,
} from 'api';
import { FullScreenSpinner } from 'components/FullScreen';
import { Counter, TextV2 } from '@insights-ltd/design-library/components';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from 'types/types';
import EvaluationsTable, {
  evaluationDataTransformer,
} from 'pages/CompletedEvaluators/EvaluationsTable/EvaluationsTable';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { EvaluatorSearchFilter } from 'api/evaluatorLinks/evaluatorLinksApiTypes';
import { sortChapters } from 'utils/mappers/sorting';
import { booleanizeCheckedItems } from 'utils/mappers/entityMappers';
import { useDebounceValue } from 'components/hooks/useDebouncedValue';
import { SearchFilter } from 'components/SearchFilter/SearchFilter';
import {
  getMinimumSearchCharacters,
  getStartsWithContainsFilterOptions,
  SearchMode,
  STARTS_WITH,
} from 'api/search';
import usePaginationV2, {
  DEFAULT_TABLE_ITEMS_PER_PAGE,
} from 'components/hooks/usePaginationV2';

const StyledTableWrapper = styled('div')(({ theme }) => ({
  margin: '0 0',
  padding: `0 ${theme.spacing(spacingSizeMap.XS)} 0`,
}));

const StyledNoEvaluations = styled(TextV2)(({ theme }) => ({
  color: theme.palette.grey[600],
  fontWeight: theme.typography.fontWeightBold,
}));

type EvaluatorResultsProps = {
  id: string;
  organisationId: string;
  checkedItems: Record<string, User>;
  setCheckedItems: (checkedItemsMap: Record<string, User>) => void;
  searchTerm: string;
  setSearchInputValue: (value: string) => void;
  searchInputValue: string;
  initialSearchFilter?: EvaluatorSearchFilter;
};

const EvaluatorResults = ({
  id,
  organisationId,
  checkedItems,
  setCheckedItems,
  searchTerm,
  setSearchInputValue,
  searchInputValue,
  initialSearchFilter = 'ALL',
}: EvaluatorResultsProps) => {
  const { t, i18n } = useTranslation();
  const { language: locale } = i18n;
  const ref = useRef<HTMLInputElement>(null);
  const [filter, setFilter] =
    useState<EvaluatorSearchFilter>(initialSearchFilter);
  const [searchMode, setSearchMode] = useState<SearchMode>(STARTS_WITH);
  const commonDataStructure = {
    fullName: t(
      'ui.event-management.evaluator-links.completed-evaluators.table-header.name',
    ),
    emailAddress: t(
      'ui.event-management.evaluator-links.completed-evaluators.table-header.email',
    ),
    referralCode: t(
      'ui.event-management.evaluator-links.completed-evaluators.table-header.referral-code',
    ),
    dateCompleted: t(
      'ui.event-management.evaluator-links.completed-evaluators.table-header.date-completed',
    ),
  };

  const dataStructure = {
    ...commonDataStructure,
    latestPurchase: t(
      'ui.event-management.evaluator-links.completed-evaluators.table-header.latest-purchase',
    ),
    colourScores: t(
      'ui.event-management.evaluator-links.completed-evaluators.table-header.colour-scores',
    ),
  };

  const categoryOptions: { label: string; value: EvaluatorSearchFilter }[] = [
    {
      label: t(
        'ui.event-management.evaluator-links.completed-evaluators.search-filter.label.all',
      ),
      value: 'ALL',
    },
    {
      label: t(
        'ui.event-management.evaluator-links.completed-evaluators.search-filter.label.purchased',
      ),
      value: 'PURCHASED',
    },
    {
      label: t(
        'ui.event-management.evaluator-links.completed-evaluators.search-filter.label.unpurchased',
      ),
      value: 'UNPURCHASED',
    },
  ];

  const debouncedSearchTerm = useDebounceValue(searchTerm);
  const minSearchLength = getMinimumSearchCharacters(searchMode);

  const {
    data: completedEvaluationsResponse,
    isPending,
    isFetching,
    status,
    hasNextPage,
    fetchNextPage,
  } = useGetCompletedEvaluators(
    id,
    debouncedSearchTerm.length >= minSearchLength ? debouncedSearchTerm : '',
    searchMode,
    filter,
  );

  const totalCount = completedEvaluationsResponse?.totalCount ?? 0;
  const apiPaginationProps = {
    isFetching,
    hasMoreToLoad: hasNextPage,
    loadMore: fetchNextPage,
    loadingState: status,
  };

  const completedEvaluations: CompletedEvaluation[] =
    completedEvaluationsResponse?.items ?? [];

  const items: FlattenedCompletedEvaluation[] = evaluationDataTransformer(
    completedEvaluations,
    locale,
  ).map(({ chapters, ...rest }) => {
    return {
      ...rest,
      chapters: chapters.sort(sortChapters).map((chapter) => chapter),
    };
  });
  const paginator = usePaginationV2(
    items,
    DEFAULT_TABLE_ITEMS_PER_PAGE,
    totalCount,
  );
  const handleSetCheckedItems = (checkedItemsMap: Record<string, boolean>) => {
    const selectedLearners = Object.keys(checkedItemsMap).reduce<
      Record<string, User>
    >((prev, itemId) => {
      const learner =
        completedEvaluations.find(({ id: evalId }) => evalId === itemId)
          ?.learner ?? checkedItems[itemId];

      if (!learner) {
        return prev;
      }
      return {
        ...prev,
        [itemId]: learner,
      };
    }, {});

    setCheckedItems(selectedLearners);
  };

  const tableElement =
    items.length > 0 ? (
      <StyledTableWrapper>
        <EvaluationsTable
          paginator={paginator}
          dataStructure={dataStructure}
          checkedItems={booleanizeCheckedItems(checkedItems)}
          onCheckedItems={handleSetCheckedItems}
          organisationId={organisationId}
          apiPaginationProps={apiPaginationProps}
        />
      </StyledTableWrapper>
    ) : (
      <StyledNoEvaluations component="span">
        {searchTerm === ''
          ? t(
              'ui.event-management.evaluator-links.completed-evaluators.no-evaluators',
            )
          : t(
              'ui.event-management.evaluator-links.completed-evaluators.no-evaluators-results',
            )}
      </StyledNoEvaluations>
    );
  const leftSelectProps = {
    options: getStartsWithContainsFilterOptions(t),
    onChange: (value: SearchMode) => {
      setSearchMode(value);
    },
    value: searchMode,
  };
  const rightSelectProps = {
    onChange: (value: EvaluatorSearchFilter) => {
      setFilter(value);
    },
    value: filter,
    options: categoryOptions,
  };

  return (
    <>
      <Container maxWidth="xl">
        <Container maxWidth="lg">
          <SearchFilter<{
            rightSelectOptionsType: EvaluatorSearchFilter;
            leftSelectOptionsType: SearchMode;
          }>
            ref={ref}
            sx={(theme) => ({
              margin: '-3rem 0 0 !important',
              padding: `${theme.spacing(spacingSizeMap.S)} !important`,
            })}
            placeholder={t(
              'ui.event-management.evaluator-links.search.placeholder',
            )}
            handleTermChange={setSearchInputValue}
            showButton={false}
            inputState={searchInputValue}
            minCharacters={minSearchLength}
            rightSelectProps={rightSelectProps}
            leftSelectProps={leftSelectProps}
            labelText={`${t('ui.event-management.list-filter.search')} ${t(
              'ui.event-management.list-filter.search.name-email-referral-code',
            )}`}
            isLabelAboveSearchInput={true}
          />
        </Container>
      </Container>
      <Container maxWidth="xl">
        {isPending ? (
          <FullScreenSpinner message={t('ui.event-management.loading')} />
        ) : (
          <Container maxWidth="lg">
            <Box
              sx={[
                {
                  maxWidth: 'lg',
                },
                (theme) => ({
                  margin: `${theme.spacing(5)} 0 ${theme.spacing(
                    spacingSizeMap.M,
                  )}`,
                  fontSize: '1.5rem',
                }),
              ]}
            >
              <TextV2
                variant="h3"
                sx={{
                  marginTop: (theme) => theme.spacing(4),
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {t(
                  'ui.event-management.evaluator-links.completed-evaluators.completed-evaluators',
                )}
                <Counter
                  count={paginator.totalCount}
                  size="large"
                  sx={{ marginLeft: (theme) => theme.spacing(1) }}
                  dataTestId="count-box"
                />
              </TextV2>
            </Box>
            {tableElement}
          </Container>
        )}
      </Container>
    </>
  );
};

export default EvaluatorResults;
